import React, { useEffect } from 'react';
import {
  X,
  FileText,
  ExternalLink,
  Download,
  Maximize2,
} from 'lucide-react';
import { PdfViewerOptions } from '../../providers/pdf-viewer-context';

interface PdfViewerModalProps {
  isOpen: boolean;
  options: PdfViewerOptions | null;
  onClose: () => void;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ isOpen, options, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Bloquear scroll de fondo cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !options) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/75 backdrop-blur-md animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={options.title}
    >
      {/* Contenedor Principal del Visor */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-2xl w-full max-w-6xl h-[92vh] sm:h-[88vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Cabecera del Visor */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-neutral-50 dark:bg-neutral-800/80 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-brand-50 dark:bg-brand-950/40 text-brand-primary flex items-center justify-center flex-shrink-0 border border-brand-200 dark:border-brand-800">
              <FileText className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-white truncate">
                {options.title}
              </h3>
              {options.subtitle ? (
                <p className="text-[11px] sm:text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {options.subtitle}
                </p>
              ) : (
                <p className="text-[11px] text-neutral-400">
                  Visor de documentos oficial BARTECH
                </p>
              )}
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Abrir en pestaña nueva */}
            <a
              href={options.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
              title="Abrir documento en pantalla completa / pestaña nueva"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Pantalla Completa</span>
            </a>

            {/* Descargar solo si el cliente lo desea */}
            <a
              href={options.url}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
              title="Descargar copia del archivo PDF"
            >
              <Download className="w-3.5 h-3.5 text-brand-primary" />
              <span className="hidden md:inline">Descargar Copia</span>
            </a>

            {/* Botón Cerrar */}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-lg text-neutral-500 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors cursor-pointer ml-1"
              aria-label="Cerrar visor de PDF"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Cuerpo del Visor de Documentos */}
        <div className="flex-1 w-full h-full relative bg-neutral-100 dark:bg-neutral-950">
          <object
            data={`${options.url}#toolbar=1&view=FitH`}
            type="application/pdf"
            className="w-full h-full border-0"
            aria-label={options.title}
          >
            <iframe
              src={`${options.url}#toolbar=1&view=FitH`}
              title={options.title}
              className="w-full h-full border-0"
            />
          </object>

          {/* Aviso y acceso directo optimizado para dispositivos móviles */}
          <div className="sm:hidden absolute bottom-3 left-3 right-3 p-3 rounded-xl bg-neutral-900/95 text-white text-xs flex items-center justify-between shadow-2xl backdrop-blur-md border border-neutral-700">
            <span className="text-[11px] text-neutral-300">Lectura móvil sin descarga</span>
            <a
              href={options.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-brand-primary hover:text-red-400 underline flex items-center gap-1 text-xs"
            >
              <span>Abrir pantalla completa</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
